import {
	Component,
	Input,
	OnChanges,
	OnDestroy,
	SimpleChanges,
} from '@angular/core';
import { ToastService } from '@core/components/toasts/toasts.service';
import moment from 'moment';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TypeOccurrenceDisciplinaryMeasure } from 'services/enums/TypeOccurrenceDisciplinaryMeasure.enum';
import { EmployeeDisciplinaryMeasureModel } from 'services/models/corporate/employee-disciplinary-measure.model';
import { QueryParams } from 'services/models/odata/queryParams';
import { WorkforceplanService } from 'services/workforceplan/workforceplan-main.service';

export type EmployeeDisciplinaryMeasureRow = {
	id: string;
	occurrenceDate: string | null;
	occurrenceType: string;
	occurrenceTypeLabel: string;
	occurrenceTypeClass: string;
	occurrenceJustification: string | null;
	occurrenceJustificationLabel: string;
	amountDays: number | null;
	note: string;
	creationDate: string;
	effectiveDate: string | null;
};

type SortProp =
	| 'creationDate'
	| 'occurrenceDate'
	| 'occurrenceTypeLabel'
	| 'occurrenceJustificationLabel'
	| 'amountDays'
	| 'note';

@Component({
	selector: 'app-employee-details-disciplinary-measure',
	templateUrl: './employee-details-disciplinary-measure.component.html',
	styleUrls: ['./employee-details-disciplinary-measure.component.scss'],
})
export class EmployeeDetailsDisciplinaryMeasureComponent implements OnChanges, OnDestroy {
	@Input() employeeId: string | null | undefined;

	statusScreen: 'loading' | 'fetched' | 'empty' | 'error' = 'loading';
	allRows: EmployeeDisciplinaryMeasureRow[] = [];
	pagedRows: EmployeeDisciplinaryMeasureRow[] = [];
	count = 0;

	queryParams: QueryParams = Object.assign(new QueryParams(), {
		top: 8,
		skip: 0,
		page: 1,
		orderBy: 'occurrenceDate',
		orderByOrder: 'desc' as 'asc' | 'desc',
	});

	currentSort: { prop: SortProp; dir: 'asc' | 'desc' } = {
		prop: 'occurrenceDate',
		dir: 'desc',
	};

	private readonly destroy$ = new Subject<void>();
	private loadSubscription?: Subscription;
	private loadedEmployeeId: string | null = null;

	constructor(
		private readonly workforceplanService: WorkforceplanService,
		private readonly toastService: ToastService,
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (!changes.employeeId) {
			return;
		}

		const employeeId = this.employeeId;
		if (!employeeId) {
			this.resetState();
			this.statusScreen = 'error';
			return;
		}

		if (employeeId !== this.loadedEmployeeId) {
			this.queryParams.skip = 0;
			this.queryParams.page = 1;
			this.loadDisciplinaryMeasures(employeeId);
		}
	}

	ngOnDestroy(): void {
		this.loadSubscription?.unsubscribe();
		this.destroy$.next();
		this.destroy$.complete();
	}

	onSort(event: { sorts?: Array<{ prop: string; dir: string }> }): void {
		if (!event?.sorts?.length) {
			return;
		}

		const [sort] = event.sorts;
		const prop = sort.prop as SortProp;
		const dir = sort.dir === 'asc' ? 'asc' : 'desc';

		this.currentSort = { prop, dir };
		this.queryParams.orderBy = prop;
		this.queryParams.orderByOrder = dir;
		this.queryParams.skip = 0;
		this.queryParams.page = 1;
		this.applySortAndPaging();
	}

	onPaginatorQuery(): void {
		const top = this.queryParams.top || 8;
		const page = this.queryParams.page || 1;
		this.queryParams.skip = top * (page - 1);
		this.applySortAndPaging();
	}

	formatDays(row: EmployeeDisciplinaryMeasureRow): string {
		if (
			row.occurrenceType === TypeOccurrenceDisciplinaryMeasure.Suspension.value &&
			row.amountDays != null &&
			row.amountDays > 0
		) {
			return String(row.amountDays);
		}

		return '-';
	}

	getOccurrenceTypeBadgeClass(row: EmployeeDisciplinaryMeasureRow): string {
		if (row.occurrenceType === TypeOccurrenceDisciplinaryMeasure.Verbal.value) {
			return 'badge-verbal-yellow';
		}

		return `bg-${row.occurrenceTypeClass || 'secondary'}`;
	}

	shouldShowNoteTooltip(row: EmployeeDisciplinaryMeasureRow, el?: HTMLElement): boolean {
		const note = (row?.note || '').trim();
		if (!note || note === '-') {
			return false;
		}

		if (!el) {
			return true;
		}

		return el.scrollHeight > el.clientHeight;
	}

	retryLoad(): void {
		if (!this.employeeId) {
			return;
		}

		this.queryParams.skip = 0;
		this.queryParams.page = 1;
		this.loadDisciplinaryMeasures(this.employeeId);
	}

	private loadDisciplinaryMeasures(employeeId: string): void {
		this.statusScreen = 'loading';
		this.loadSubscription?.unsubscribe();

		this.loadSubscription = this.workforceplanService
			.listDisciplinaryMeasuresByEmployee(employeeId)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (items) => {
					this.loadedEmployeeId = employeeId;
					this.allRows = (items || []).map((item) => this.mapItem(item));
					this.applySortAndPaging();
				},
				error: () => {
					this.resetState();
					this.statusScreen = 'error';
					this.toastService.showError(
						'Não foi possível carregar as advertências do colaborador.'
					);
				},
			});
	}

	private applySortAndPaging(): void {
		const sorted = this.sortRows([...this.allRows]);
		this.count = sorted.length;

		const top = this.queryParams.top || 8;
		const skip = this.queryParams.skip || 0;
		this.pagedRows = sorted.slice(skip, skip + top);
		this.statusScreen = this.count > 0 ? 'fetched' : 'empty';
	}

	private sortRows(rows: EmployeeDisciplinaryMeasureRow[]): EmployeeDisciplinaryMeasureRow[] {
		const prop = this.currentSort?.prop || 'occurrenceDate';
		const dir = this.currentSort?.dir === 'asc' ? 1 : -1;

		return rows.sort((a, b) => {
			if (prop === 'creationDate' || prop === 'occurrenceDate') {
				const aValue = moment(a.occurrenceDate || a.creationDate).valueOf();
				const bValue = moment(b.occurrenceDate || b.creationDate).valueOf();
				return (aValue - bValue) * dir;
			}

			if (prop === 'amountDays') {
				const aValue = a.amountDays ?? -1;
				const bValue = b.amountDays ?? -1;
				return (aValue - bValue) * dir;
			}

			const aText = String(a[prop] ?? '');
			const bText = String(b[prop] ?? '');
			return aText.localeCompare(bText, 'pt-BR') * dir;
		});
	}

	private mapItem(item: EmployeeDisciplinaryMeasureModel): EmployeeDisciplinaryMeasureRow {
		const occurrenceTypeEnum = item.occurrenceTypeEnum;
		const occurrenceJustificationEnum = item.occurrenceJustificationEnum;
		const occurrenceType = occurrenceTypeEnum?.value || String(item.occurrenceType || '').trim();
		const occurrenceJustification = occurrenceJustificationEnum?.value
			|| (item.occurrenceJustification != null ? String(item.occurrenceJustification).trim() : '')
			|| null;

		return {
			id: item.id || item.movementRequestId || '',
			occurrenceDate: item.occurrenceDate || null,
			occurrenceType,
			occurrenceTypeLabel: occurrenceTypeEnum?.label || occurrenceType || '-',
			occurrenceTypeClass: occurrenceTypeEnum?.className || 'secondary',
			occurrenceJustification,
			occurrenceJustificationLabel: occurrenceJustificationEnum?.label || occurrenceJustification || '-',
			amountDays: this.parseAmountDays(item.amountDays),
			note: (item.note || '').trim() || '-',
			creationDate: item.creationDate || item.occurrenceDate || '',
			effectiveDate: item.effectiveDate || null,
		};
	}

	private parseAmountDays(value: number | string | undefined): number | null {
		if (value == null || value === '') {
			return null;
		}

		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : null;
	}

	private resetState(): void {
		this.allRows = [];
		this.pagedRows = [];
		this.count = 0;
		this.loadedEmployeeId = null;
	}
}
